/**
 * Client-side reproduction of the PDF report's "Calorie & macronutrient
 * distribution" section (report-engine/src/reports/
 * calorie-and-macronutrient-distribution-section.ts), built WITHOUT the slow
 * `get_food_report_data_preview` endpoint.
 *
 * Data comes from two fast endpoints the portal already uses:
 *   1. nutrition_details_table grouped by `meal_id` — per-meal nutrient sums
 *      (incl. the fatty-acid split needed for the macro chart).
 *   2. useMeals — supplies `meal_id → meal_time` so each meal can be bucketed
 *      into its clock time-of-day period.
 *
 * The period boundaries mirror MNT's `get_patient_meal_period_from_datetime`
 * (app/models.py:10006) exactly, and the fat split mirrors
 * `_compute_healthy_unhealthy_fat_for_med_score_history`
 * (app/api/patient_reports/sections/mind.py:686).
 */
import type { NutritionAggVal, NutritionDetailsRow } from '@/api/reports';
import type { DisaggregatedMeal, Meal } from '@/api/meals';

export type MealPeriodKey =
  | 'morning'
  | 'lunchtime'
  | 'afternoon'
  | 'dinnertime'
  | 'evening';

export type MealPeriod = {
  key: MealPeriodKey;
  /** i18n label, e.g. "Morning". */
  label: string;
  /** i18n clock-range sublabel, e.g. "5 - 11 AM". */
  time: string;
};

/** Ordered to match the report (top → bottom). Labels/time are i18n keys. */
export const MEAL_PERIODS: MealPeriod[] = [
  { key: 'morning', label: 'Morning', time: '5 - 11 AM' },
  { key: 'lunchtime', label: 'Lunchtime', time: '11 AM - 2 PM' },
  { key: 'afternoon', label: 'Afternoon', time: '2 - 4:30 PM' },
  { key: 'dinnertime', label: 'Dinnertime', time: '4:30 - 9 PM' },
  { key: 'evening', label: 'Evening', time: '9 PM - 5 AM' },
];

/**
 * Bucket a `HH:MM[:SS]` local meal time into its report period. Mirrors
 * MNT's `get_patient_meal_period_from_datetime` — note the half-hour
 * boundaries at 4:30 PM use hour + minute/60.
 */
export function getMealPeriod(mealTime: string | null | undefined): MealPeriodKey {
  if (!mealTime) return 'evening';
  const [hStr, mStr] = mealTime.split(':');
  const hour = Number(hStr);
  const minute = Number(mStr ?? 0);
  if (Number.isNaN(hour)) return 'evening';
  const hm = hour + minute / 60;
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 14) return 'lunchtime';
  if (hm >= 14 && hm < 16.5) return 'afternoon';
  if (hm >= 16.5 && hm < 21) return 'dinnertime';
  return 'evening'; // 21:00–05:00 (both the 9 PM–midnight and midnight–5 AM arms)
}

/** A selectable nutrient for the timing chart. */
export type TimingNutrient = {
  key: string;
  /** i18n display name, e.g. "Calories". */
  label: string;
  /** Field name on the nutrition-details row (matches DBMealItem column). */
  field: string;
  /** i18n unit shown in the per-period average, e.g. "kcal", "g", "mg". */
  unit: string;
  /** Bar colour (hex). */
  colour: string;
};

/**
 * The starter set, in menu order. `field` names match the aggregated columns
 * on PatientNutritionDetailsTable (MNT/app/api/patient_reports/api.py:150).
 */
export const TIMING_NUTRIENTS: TimingNutrient[] = [
  { key: 'kcal', label: 'Calories', field: 'energy_kcal', unit: 'kcal', colour: '#FFDC00' },
  { key: 'protein', label: 'Protein', field: 'protein_g', unit: 'g', colour: '#FF9024' },
  { key: 'fat', label: 'Fat', field: 'fat_g', unit: 'g', colour: '#528EC3' },
  { key: 'carbs', label: 'Carbohydrates', field: 'carbohydrate_g', unit: 'g', colour: '#FCBA03' },
  { key: 'fiber', label: 'Fiber', field: 'fiber_g', unit: 'g', colour: '#4CAF7D' },
  { key: 'netcarbs', label: 'Net carbs', field: 'netcarb_g', unit: 'g', colour: '#C9A227' },
  { key: 'sodium', label: 'Sodium', field: 'sodium_mg', unit: 'mg', colour: '#A0728C' },
  { key: 'potassium', label: 'Potassium', field: 'potassium_mg', unit: 'mg', colour: '#6B8E9E' },
  { key: 'calcium', label: 'Calcium', field: 'calcium_mg', unit: 'mg', colour: '#7D82C4' },
];

/**
 * Tolerant sum extraction — the nutrition-details endpoint returns each
 * nutrient as either a nested `{ sum }` aggregate or a flat `<field>_sum`
 * number, depending on version. Same shape-tolerance the Summary KPI uses.
 *
 * Accepts `undefined` (returns 0) so call sites holding a possibly-absent
 * row don't each need their own guard — this is the single accessor for
 * the endpoint's dual-shape nutrient values.
 */
export function nutrientSum(
  row: NutritionDetailsRow | undefined,
  field: string,
): number {
  if (!row) return 0;
  const nested = row[field];
  if (typeof nested === 'object' && nested !== null) {
    return (nested as NutritionAggVal).sum ?? 0;
  }
  const flat = row[`${field}_sum`];
  return typeof flat === 'number' ? flat : 0;
}

/** Nutrient fields summed from disaggregated items, matching DBMealItem columns. */
const AGG_FIELDS = [
  'energy_kcal',
  'protein_g',
  'fat_g',
  'carbohydrate_g',
  'fiber_g',
  'netcarb_g',
  'sodium_mg',
  'potassium_mg',
  'calcium_mg',
  'fattyacids_totalsaturated_g',
  'fattyacids_totaltrans_g',
] as const;

/**
 * Collapse one disaggregated meal (its component items) into a per-meal row
 * shaped like a nutrition-details row (`<field>_sum` + meal_id/meal_date), so
 * the same period/macro calculations run unchanged — just off disaggregated
 * data (composites split into components) instead of raw meal items.
 */
export function disaggregatedMealToRow(meal: DisaggregatedMeal): NutritionDetailsRow {
  const sums: Record<string, number> = {};
  for (const item of meal.meal_items ?? []) {
    const n = item.nutrition_response ?? {};
    for (const field of AGG_FIELDS) {
      const value = n[field];
      sums[`${field}_sum`] = (sums[`${field}_sum`] ?? 0) + (typeof value === 'number' ? value : 0);
    }
  }
  return { meal_id: meal.id, meal_date: meal.meal_date, ...sums } as NutritionDetailsRow;
}

export type PeriodDatum = {
  key: MealPeriodKey;
  /** Fraction of the day's total for this nutrient (0–1). */
  pct: number;
  /** Average amount per day-with-data, in the nutrient's unit. */
  avg: number;
};

/**
 * Join per-meal nutrient rows to their meal times and roll the chosen nutrient
 * up by clock period. `%` = period ÷ day-total (report definition); `avg` =
 * period ÷ number of days with logged meals.
 */
export function nutrientByPeriod(
  meals: Meal[],
  rows: NutritionDetailsRow[],
  field: string,
): { periods: PeriodDatum[]; nDays: number; total: number } {
  const timeByMeal = new Map<number, string>();
  const datesWithData = new Set<string>();
  for (const m of meals) {
    timeByMeal.set(m.id, m.meal_time);
  }

  const sums: Record<MealPeriodKey, number> = {
    morning: 0,
    lunchtime: 0,
    afternoon: 0,
    dinnertime: 0,
    evening: 0,
  };

  for (const row of rows) {
    const mealId = typeof row.meal_id === 'number' ? row.meal_id : null;
    if (mealId === null) continue;
    const mealTime = timeByMeal.get(mealId);
    const value = nutrientSum(row, field);
    const period = getMealPeriod(mealTime);
    sums[period] += value;
    // Count a day as "with data" only when the meal actually contributes.
    const meal = mealById(meals, mealId);
    if (meal?.meal_date && value > 0) datesWithData.add(meal.meal_date);
  }

  const nDays = datesWithData.size;
  const total = MEAL_PERIODS.reduce((acc, p) => acc + sums[p.key], 0);
  const periods: PeriodDatum[] = MEAL_PERIODS.map((p) => ({
    key: p.key,
    pct: total > 0 ? sums[p.key] / total : 0,
    avg: nDays > 0 ? sums[p.key] / nDays : 0,
  }));

  return { periods, nDays, total };
}

function mealById(meals: Meal[], id: number): Meal | undefined {
  // Small lists (a date range of meals) — linear scan is fine and avoids
  // building a second index the callers don't need.
  return meals.find((m) => m.id === id);
}

const KCAL_PER_G_CARB = 4;
const KCAL_PER_G_PROTEIN = 4;
const KCAL_PER_G_FAT = 9;

export type MacroKey = 'carbs' | 'protein' | 'unsaturated' | 'saturated';

export type MacroDatum = {
  key: MacroKey;
  label: string;
  /** Fraction of macro energy (0–1). The four sum to 1 by construction. */
  pct: number;
  colour: string;
  /** AMDR reference band as [min, max] fractions, for the bracket markers. */
  amdr: [number, number];
};

/**
 * Aggregate the whole range into the 4-bar macronutrient distribution
 * (% of energy via Atwater 4/4/9). Fat is split saturated vs unsaturated by
 * mirroring MNT: saturated = saturated + trans; unsaturated is whatever total
 * fat is left over (so it's always defined even when the mono/poly breakdown
 * is missing, and saturated can never exceed total fat).
 */
export function macroDistribution(rows: NutritionDetailsRow[]): {
  macros: MacroDatum[];
  hasData: boolean;
} {
  let carbG = 0;
  let proteinG = 0;
  let fatG = 0;
  let satG = 0;
  let transG = 0;
  for (const row of rows) {
    carbG += nutrientSum(row, 'carbohydrate_g');
    proteinG += nutrientSum(row, 'protein_g');
    fatG += nutrientSum(row, 'fat_g');
    satG += nutrientSum(row, 'fattyacids_totalsaturated_g');
    transG += nutrientSum(row, 'fattyacids_totaltrans_g');
  }

  const carbKcal = carbG * KCAL_PER_G_CARB;
  const proteinKcal = proteinG * KCAL_PER_G_PROTEIN;
  const totalFatKcal = fatG * KCAL_PER_G_FAT;
  // Anchor on saturated (+trans); clamp so it can't exceed total fat.
  const satKcal = Math.min((satG + transG) * KCAL_PER_G_FAT, totalFatKcal);
  const unsatKcal = Math.max(totalFatKcal - satKcal, 0);

  const denom = carbKcal + proteinKcal + totalFatKcal;
  const frac = (kcal: number) => (denom > 0 ? kcal / denom : 0);

  const macros: MacroDatum[] = [
    { key: 'carbs', label: 'Carbohydrates', pct: frac(carbKcal), colour: '#FFDC00', amdr: [0.45, 0.65] },
    { key: 'protein', label: 'Protein', pct: frac(proteinKcal), colour: '#FF9024', amdr: [0.1, 0.35] },
    { key: 'unsaturated', label: 'Unsaturated fat', pct: frac(unsatKcal), colour: '#528EC3', amdr: [0.2, 0.35] },
    { key: 'saturated', label: 'Saturated fat', pct: frac(satKcal), colour: '#8085F2', amdr: [0, 0.1] },
  ];

  return { macros, hasData: denom > 0 };
}
